import React from "react";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { getMutationSchemaById } from "../../lib/kevia-mutations/mutationSchemas";

/**
 * FASE 21.5 — CONFIRMATION FLOW
 *
 * Calm, non-modal confirmation card shown before any Kevia write action.
 * Inline in the chat thread — never a fullscreen blocker.
 *
 * @param {Object} props
 * @param {string}   props.toolId
 * @param {Object}   props.payload        — extracted fields (display only)
 * @param {string}   [props.entityName]   — human name of affected entity
 * @param {Function} props.onConfirm
 * @param {Function} props.onCancel
 * @param {boolean}  [props.loading]
 * @param {'success'|'error'|null} [props.outcome]
 * @param {string}   [props.outcomeMessage]
 */
export function KeviaConfirmActionCard({
  toolId,
  payload,
  entityName,
  onConfirm,
  onCancel,
  loading   = false,
  outcome   = null,
  outcomeMessage,
}) {
  const schema = getMutationSchemaById(toolId);
  if (!schema) return null;

  /* ─── Outcome state (after confirm/cancel) ───── */

  if (outcome === "success") {
    return (
      <div className="
        flex items-start gap-2.5
        px-4 py-3 rounded-[16px]
        bg-emerald-50/80 dark:bg-emerald-900/15
        border border-emerald-200/60 dark:border-emerald-700/25
        animate-fade
      ">
        <CheckCircle2 size={15} className="text-emerald-500 dark:text-emerald-400 shrink-0 mt-0.5" />
        <p className="text-[13px] text-emerald-700 dark:text-emerald-300 leading-snug">
          {outcomeMessage ?? "Acción completada correctamente."}
        </p>
      </div>
    );
  }

  if (outcome === "error") {
    return (
      <div className="
        flex items-start gap-2.5
        px-4 py-3 rounded-[16px]
        bg-red-50/50 dark:bg-red-900/10
        border border-red-200/50 dark:border-red-700/20
        animate-fade
      ">
        <XCircle size={15} className="text-red-400 dark:text-red-500 shrink-0 mt-0.5" />
        <p className="text-[13px] text-red-600 dark:text-red-400 leading-snug">
          {outcomeMessage ?? "Kevia no pudo completar la acción."}
        </p>
      </div>
    );
  }

  /* ─── Confirmation UI ──────────────────────────── */

  const summaryFields = buildSummaryFields(toolId, payload, entityName);

  return (
    <div className="
      px-4 py-3.5 rounded-[16px]
      bg-[#f0f6ff] dark:bg-slate-800/60
      border border-[#1b4f9c]/15 dark:border-sky-500/20
      shadow-[0_2px_12px_rgba(27,79,156,0.05)]
      space-y-3
      animate-fade
    ">
      {/* Header */}
      <div className="flex items-start gap-2.5">
        <div className="h-7 w-7 rounded-[9px] bg-[#1b4f9c]/10 dark:bg-sky-500/15 flex items-center justify-center shrink-0 mt-0.5">
          <span className="text-[13px]">{schema.entityType === "prayer" ? "🙏" : schema.entityType === "event" ? "📅" : schema.entityType === "note" ? "📝" : "📌"}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-slate-700 dark:text-slate-200 leading-snug">
            {schema.label}
          </p>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">
            {schema.description}
            {entityName ? ` — ${entityName}` : ""}
          </p>
        </div>
      </div>

      {/* Summary fields */}
      {summaryFields.length > 0 && (
        <div className="space-y-1 pl-[38px]">
          {summaryFields.map(({ label, value }) => (
            <div key={label} className="flex items-center gap-1.5">
              <span className="text-[10px] text-slate-400 dark:text-slate-500 w-20 shrink-0">{label}</span>
              <span className="text-[12px] text-slate-600 dark:text-slate-300 font-medium truncate">{value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-2 pl-[38px]">
        <button
          onClick={onConfirm}
          disabled={loading}
          className="
            flex items-center gap-1.5
            px-4 py-1.5 rounded-full
            bg-[#1b4f9c] text-white
            text-[12px] font-semibold
            shadow-[0_2px_8px_rgba(27,79,156,0.2)]
            transition-all duration-200
            hover:bg-[#163e7a]
            active:scale-[0.97]
            disabled:opacity-50 disabled:cursor-not-allowed
          "
        >
          {loading ? (
            <>
              <Loader2 size={11} className="animate-spin" />
              Procesando…
            </>
          ) : (
            "Confirmar"
          )}
        </button>
        <button
          onClick={onCancel}
          disabled={loading}
          className="
            px-3.5 py-1.5 rounded-full
            text-slate-500 dark:text-slate-400
            text-[12px] font-medium
            hover:text-slate-700 dark:hover:text-slate-200
            transition-colors duration-150
            disabled:opacity-40
          "
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}

/* ─── Summary field builders ──────────────────────────── */

function buildSummaryFields(toolId, payload, entityName) {
  const fields = [];
  if (!payload) return fields;

  const push = (label, value) => { if (value) fields.push({ label, value }); };

  switch (toolId) {
    case "create_visit":
    case "schedule_followup":
      push("Persona/familia", payload.contact_name || entityName);
      push("Fecha",           payload.date || payload.followup_date);
      push("Tipo",            payload.visit_type);
      break;
    case "create_prayer":
      push("Persona/familia", payload.contact_name || entityName);
      push("Tipo oración",    payload.prayer_type);
      break;
    case "create_event":
      push("Nombre",    payload.title);
      push("Fecha",     payload.date);
      push("Tipo",      payload.event_type);
      push("Lugar",     payload.location_name);
      break;
    case "create_family_note":
      push("Entidad", entityName || payload.entity_id?.slice(0, 8));
      push("Tipo",    payload.entity_type);
      break;
    case "update_visit_status":
      push("Nuevo estado", payload.new_status);
      break;
    case "update_person_status":
      push("Campo",  payload.status_field);
      push("Valor",  payload.new_value);
      break;
    default:
      break;
  }

  return fields;
}
